import React, { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { API_BASE, formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    UploadCloud, FileText, Trash2, Download, Eye, Share2, RefreshCw,
    History, Archive, ArchiveRestore, Pencil, Plus, FileImage, FileSpreadsheet,
    FileArchive, FileType2,
} from "lucide-react";
import { fmtDateTime, fromNow } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const VISIBILITY_LABELS = {
    admin_only: "Admin Only",
    assigned_employee: "Assigned Employee",
    entire_firm: "Entire Firm",
    custom: "Custom Users",
    employee_own: "Employee Upload",
};
const VIS_STYLES = {
    admin_only: "bg-slate-100 text-slate-700 border-slate-200",
    assigned_employee: "bg-blue-50 text-primary border-blue-200",
    entire_firm: "bg-emerald-50 text-emerald-700 border-emerald-200",
    custom: "bg-violet-50 text-violet-700 border-violet-200",
    employee_own: "bg-amber-50 text-amber-800 border-amber-200",
};
const LINK_LABELS = {
    client: "Client",
    task: "Task",
    compliance: "Compliance",
    invoice: "Invoice",
    general: "General Document",
};

const EMPTY_UPLOAD = {
    file: null,
    name: "",
    linked_to: "general",
    client_id: "",
    task_id: "",
    compliance_id: "",
    invoice_id: "",
    category: "",
    description: "",
    visibility: "admin_only",
    shared_with: [],
};

const EMPTY_REQUEST = {
    employee_id: "",
    client_id: "",
    title: "",
    required_docs: "",
    due_date: "",
    remarks: "",
};

function iconFor(name = "", type = "") {
    const ext = (name.split(".").pop() || "").toLowerCase();
    if (["jpg","jpeg","png","gif","webp","heic"].includes(ext)) return FileImage;
    if (["xls","xlsx","csv"].includes(ext)) return FileSpreadsheet;
    if (["zip"].includes(ext)) return FileArchive;
    if (ext === "pdf") return FileText;
    return FileType2;
}

async function authedFetch(path) {
    const token = localStorage.getItem("ca_access_token");
    return fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
}

export default function Documents() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [tab, setTab] = useState("all");
    const [docs, setDocs] = useState([]);
    const [clients, setClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [categories, setCategories] = useState({ builtin: [], custom: [] });
    const [tasks, setTasks] = useState([]);
    const [q, setQ] = useState("");
    const [fClient, setFClient] = useState("all");
    const [fCategory, setFCategory] = useState("all");
    const [fVisibility, setFVisibility] = useState("all");
    const [fUploader, setFUploader] = useState("all");
    const [fStatus, setFStatus] = useState("active");

    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD);
    const [uploading, setUploading] = useState(false);

    const [historyDoc, setHistoryDoc] = useState(null);
    const [historyItems, setHistoryItems] = useState([]);

    const [shareDoc, setShareDoc] = useState(null);
    const [shareForm, setShareForm] = useState({ visibility: "admin_only", shared_with: [] });

    const [renameDoc, setRenameDoc] = useState(null);
    const [renameName, setRenameName] = useState("");

    const replaceRef = useRef(null);
    const [replaceTarget, setReplaceTarget] = useState(null);

    const [requests, setRequests] = useState([]);
    const [reqOpen, setReqOpen] = useState(false);
    const [reqForm, setReqForm] = useState(EMPTY_REQUEST);
    const [fulfillTarget, setFulfillTarget] = useState(null);

    const loadDocs = () => {
        const params = {};
        if (q) params.q = q;
        if (fClient !== "all") params.client_id = fClient;
        if (fCategory !== "all") params.category = fCategory;
        if (fVisibility !== "all") params.visibility = fVisibility;
        if (fUploader !== "all") params.uploaded_by = fUploader;
        if (fStatus !== "all") params.status = fStatus;
        return api.get("/documents", { params }).then((r) => setDocs(r.data));
    };
    const loadRequests = () => api.get("/documents/requests/list").then((r) => setRequests(r.data));

    useEffect(() => {
        loadDocs();
        loadRequests();
        api.get("/clients").then((r) => setClients(r.data.items || []));
        api.get("/documents/categories").then((r) => setCategories(r.data));
        if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
        api.get("/tasks").then((r) => setTasks(r.data.items || [])).catch(() => {});
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const t = setTimeout(loadDocs, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q, fClient, fCategory, fVisibility, fUploader, fStatus]);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
    const empName = (id) => employees.find((e) => e.id === id)?.name || id?.slice(0, 6);

    const allCategories = [...(categories.builtin || []), ...(categories.custom || [])];

    // ---------- Upload ----------
    const openUpload = (req = null) => {
        if (req) {
            setUploadForm({
                ...EMPTY_UPLOAD,
                linked_to: req.client_id ? "client" : "general",
                client_id: req.client_id || "",
                description: req.title,
                visibility: "admin_only",
            });
            setFulfillTarget(req);
        } else {
            setUploadForm(EMPTY_UPLOAD);
            setFulfillTarget(null);
        }
        setUploadOpen(true);
    };

    const doUpload = async () => {
        if (!uploadForm.file) {
            toast.error("Please select a file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", uploadForm.file);
            if (uploadForm.name) fd.append("name", uploadForm.name);
            fd.append("linked_to", uploadForm.linked_to);
            if (uploadForm.client_id) fd.append("client_id", uploadForm.client_id);
            if (uploadForm.task_id) fd.append("task_id", uploadForm.task_id);
            if (uploadForm.compliance_id) fd.append("compliance_id", uploadForm.compliance_id);
            if (uploadForm.category) fd.append("category", uploadForm.category);
            if (uploadForm.description) fd.append("description", uploadForm.description);
            if (uploadForm.visibility) fd.append("visibility", uploadForm.visibility);
            if (uploadForm.shared_with?.length)
                fd.append("shared_with", uploadForm.shared_with.join(","));
            if (fulfillTarget) fd.append("request_id", fulfillTarget.id);
            await api.post("/documents/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Uploaded");
            setUploadOpen(false);
            setFulfillTarget(null);
            loadDocs();
            loadRequests();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    // ---------- Actions ----------
    const preview = async (d) => {
        try {
            const r = await authedFetch(`/documents/${d.id}/preview`);
            if (!r.ok) throw new Error("preview failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const download = async (d) => {
        try {
            const r = await authedFetch(`/documents/${d.id}/download`);
            if (!r.ok) throw new Error("download failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = d.original_name || d.name;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed");
        }
    };
    const remove = async (d) => {
        if (!window.confirm(`Delete "${d.name}"?`)) return;
        try {
            await api.delete(`/documents/${d.id}`);
            toast.success("Deleted");
            loadDocs();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const archiveDoc = async (d) => {
        await api.post(`/documents/${d.id}/archive`);
        toast.success("Archived");
        loadDocs();
    };
    const restoreDoc = async (d) => {
        await api.post(`/documents/${d.id}/restore`);
        toast.success("Restored");
        loadDocs();
    };
    const openReplace = (d) => {
        setReplaceTarget(d);
        replaceRef.current?.click();
    };
    const doReplace = async (file) => {
        if (!file || !replaceTarget) return;
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/documents/${replaceTarget.id}/replace`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Replaced — new version created`);
            setReplaceTarget(null);
            loadDocs();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const openHistory = async (d) => {
        setHistoryDoc(d);
        const r = await api.get(`/documents/${d.id}/history`);
        setHistoryItems(r.data);
    };
    const openShare = (d) => {
        setShareDoc(d);
        setShareForm({ visibility: d.visibility || "admin_only", shared_with: d.shared_with || [] });
    };
    const saveShare = async () => {
        try {
            await api.patch(`/documents/${shareDoc.id}`, shareForm);
            toast.success("Sharing updated");
            setShareDoc(null);
            loadDocs();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const doRename = async () => {
        if (!renameName.trim()) return;
        try {
            await api.patch(`/documents/${renameDoc.id}`, { name: renameName.trim() });
            toast.success("Renamed");
            setRenameDoc(null);
            loadDocs();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // ---------- Requests ----------
    const submitRequest = async () => {
        try {
            const payload = {
                ...reqForm,
                required_docs: reqForm.required_docs
                    .split(/[,\n]/)
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            await api.post("/documents/requests", payload);
            toast.success("Request created");
            setReqOpen(false);
            setReqForm(EMPTY_REQUEST);
            loadRequests();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const reqApprove = async (r) => {
        await api.post(`/documents/requests/${r.id}/approve`);
        toast.success("Approved");
        loadRequests();
    };
    const reqReject = async (r) => {
        const reason = window.prompt("Reason for rejection?") || "";
        await api.post(`/documents/requests/${r.id}/reject`, { reason });
        toast.success("Rejected");
        loadRequests();
    };

    // ---------- Derived ----------
    const visibleDocs = useMemo(() => docs, [docs]);

    const pendingRequests = requests.filter((r) =>
        ["requested", "under_review"].includes(r.status),
    );

    return (
        <div className="space-y-6" data-testid="documents-page">
            <PageHeader
                title="Documents"
                subtitle="Client files, task attachments, invoices — versioned, categorised, permissioned."
                actions={
                    <div className="flex gap-2">
                        {isAdmin && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setReqForm(EMPTY_REQUEST);
                                    setReqOpen(true);
                                }}
                                data-testid="new-request-btn"
                            >
                                <Plus size={15} className="mr-1.5" /> Request document
                            </Button>
                        )}
                        <Button
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => openUpload()}
                            data-testid="upload-btn"
                        >
                            <UploadCloud size={16} className="mr-1.5" /> Upload
                        </Button>
                    </div>
                }
            />

            <input
                ref={replaceRef}
                type="file"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && doReplace(e.target.files[0])}
            />

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="all" data-testid="tab-docs">All documents</TabsTrigger>
                    <TabsTrigger value="requests" data-testid="tab-requests">
                        Requests {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {tab === "all" && (
                <>
                    <Card className="p-3 flex flex-wrap gap-2 items-center">
                        <Input
                            placeholder="Search name or description…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="max-w-xs"
                            data-testid="doc-search"
                        />
                        <FilterSelect value={fClient} onChange={setFClient} label="Client" all>
                            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </FilterSelect>
                        <FilterSelect value={fCategory} onChange={setFCategory} label="Category" all>
                            {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </FilterSelect>
                        {isAdmin && (
                            <FilterSelect value={fVisibility} onChange={setFVisibility} label="Visibility" all>
                                {Object.entries(VISIBILITY_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                            </FilterSelect>
                        )}
                        {isAdmin && (
                            <FilterSelect value={fUploader} onChange={setFUploader} label="Uploaded by" all>
                                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                            </FilterSelect>
                        )}
                        <FilterSelect value={fStatus} onChange={setFStatus} label="Status">
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </FilterSelect>
                    </Card>

                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Document</th>
                                        <th className="text-left px-4 py-3 font-semibold">Linked to</th>
                                        <th className="text-left px-4 py-3 font-semibold">Category</th>
                                        <th className="text-left px-4 py-3 font-semibold">Visibility</th>
                                        <th className="text-left px-4 py-3 font-semibold">Uploaded</th>
                                        <th className="px-4 py-3" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleDocs.map((d) => {
                                        const Icon = iconFor(d.original_name, d.content_type);
                                        const linkedLbl = LINK_LABELS[d.linked_to || (d.client_id ? "client" : d.task_id ? "task" : "general")];
                                        const linkedName = d.client_id ? clientName(d.client_id) : d.task_id ? "Task" : "";
                                        return (
                                            <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`doc-row-${d.id}`}>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Icon size={16} className="text-primary shrink-0" />
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-slate-800 truncate">
                                                                {d.name || d.original_name}
                                                                {d.version > 1 && (
                                                                    <span className="ml-2 text-[10px] font-mono text-slate-400">
                                                                        v{d.version}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {d.description && (
                                                                <div className="text-xs text-slate-500 truncate max-w-md">
                                                                    {d.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-slate-600">
                                                    <div>{linkedLbl}</div>
                                                    {linkedName && <div className="text-xs text-slate-500">{linkedName}</div>}
                                                </td>
                                                <td className="px-4 py-3 text-slate-600">
                                                    {d.category ? (
                                                        <span className="badge-status bg-slate-50 text-slate-700 border-slate-200">
                                                            {d.category}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1 flex-wrap">
                                                        <span
                                                            className={`badge-status ${VIS_STYLES[d.visibility] || VIS_STYLES.admin_only}`}
                                                        >
                                                            {VISIBILITY_LABELS[d.visibility] || "Admin Only"}
                                                        </span>
                                                        {d.uploaded_by_role === "employee" && (
                                                            <span className="badge-status bg-amber-50 text-amber-800 border-amber-200">
                                                                Employee Upload
                                                            </span>
                                                        )}
                                                        {(d.shared_with?.length || 0) > 0 && (
                                                            <span className="badge-status bg-blue-50 text-primary border-blue-200">
                                                                Shared
                                                            </span>
                                                        )}
                                                        {d.status === "archived" && (
                                                            <span className="badge-status bg-slate-100 text-slate-500 border-slate-300">
                                                                Archived
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-500">
                                                    <div>{d.uploaded_by_name || "—"}</div>
                                                    <div>{fromNow(d.created_at)}</div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex justify-end gap-0.5">
                                                        <IconBtn onClick={() => preview(d)} title="Preview"><Eye size={13} /></IconBtn>
                                                        <IconBtn onClick={() => download(d)} title="Download"><Download size={13} /></IconBtn>
                                                        {(isAdmin || d.uploaded_by === user?.id) && (
                                                            <IconBtn onClick={() => openReplace(d)} title="Replace"><RefreshCw size={13} /></IconBtn>
                                                        )}
                                                        <IconBtn onClick={() => openHistory(d)} title="Version history"><History size={13} /></IconBtn>
                                                        {isAdmin && (
                                                            <>
                                                                <IconBtn onClick={() => openShare(d)} title="Share / visibility"><Share2 size={13} /></IconBtn>
                                                                <IconBtn onClick={() => { setRenameDoc(d); setRenameName(d.name || d.original_name); }} title="Rename"><Pencil size={13} /></IconBtn>
                                                                {d.status === "archived" ? (
                                                                    <IconBtn onClick={() => restoreDoc(d)} title="Restore"><ArchiveRestore size={13} /></IconBtn>
                                                                ) : (
                                                                    <IconBtn onClick={() => archiveDoc(d)} title="Archive"><Archive size={13} /></IconBtn>
                                                                )}
                                                                <IconBtn onClick={() => remove(d)} title="Delete" danger><Trash2 size={13} /></IconBtn>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {visibleDocs.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="text-center py-12 text-slate-500">
                                                No documents match these filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </>
            )}

            {tab === "requests" && (
                <Card className="overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Request</th>
                                <th className="text-left px-4 py-3 font-semibold">Employee</th>
                                <th className="text-left px-4 py-3 font-semibold">Client</th>
                                <th className="text-left px-4 py-3 font-semibold">Due</th>
                                <th className="text-left px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100" data-testid={`req-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-800">{r.title}</div>
                                        {r.required_docs?.length > 0 && (
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                {r.required_docs.join(" · ")}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">{empName(r.employee_id)}</td>
                                    <td className="px-4 py-3 text-slate-600">{clientName(r.client_id)}</td>
                                    <td className="px-4 py-3 text-slate-600">{r.due_date || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className="badge-status bg-slate-50 text-slate-700 border-slate-200 capitalize">
                                            {r.status.replace("_", " ")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            {user?.role === "employee" && ["requested", "rejected"].includes(r.status) && (
                                                <Button size="sm" variant="outline" onClick={() => openUpload(r)} data-testid={`fulfill-${r.id}`}>
                                                    <UploadCloud size={13} className="mr-1" /> Upload
                                                </Button>
                                            )}
                                            {isAdmin && r.status === "under_review" && (
                                                <>
                                                    <Button size="sm" onClick={() => reqApprove(r)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                                        Approve
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => reqReject(r)}>
                                                        Reject
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {requests.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-12 text-slate-500">
                                        No document requests yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>
            )}

            {/* Upload dialog */}
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {fulfillTarget ? `Upload for: ${fulfillTarget.title}` : "Upload document"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <F label="File">
                            <Input
                                type="file"
                                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                                data-testid="upload-file"
                            />
                        </F>
                        <F label="Display name (optional)">
                            <Input
                                value={uploadForm.name}
                                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                                placeholder="Falls back to file name"
                            />
                        </F>
                        <F label="Link to">
                            <Select
                                value={uploadForm.linked_to}
                                onValueChange={(v) => setUploadForm({ ...uploadForm, linked_to: v })}
                            >
                                <SelectTrigger data-testid="upload-linked-to"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="client">Client</SelectItem>
                                    <SelectItem value="task">Task</SelectItem>
                                    <SelectItem value="compliance">Compliance</SelectItem>
                                    <SelectItem value="invoice">Invoice</SelectItem>
                                    <SelectItem value="general">General Document</SelectItem>
                                </SelectContent>
                            </Select>
                        </F>
                        {uploadForm.linked_to === "client" && (
                            <F label="Client">
                                <Select
                                    value={uploadForm.client_id}
                                    onValueChange={(v) => setUploadForm({ ...uploadForm, client_id: v })}
                                >
                                    <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                                    <SelectContent>
                                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </F>
                        )}
                        {uploadForm.linked_to === "task" && (
                            <F label="Task">
                                <Select
                                    value={uploadForm.task_id}
                                    onValueChange={(v) => setUploadForm({ ...uploadForm, task_id: v })}
                                >
                                    <SelectTrigger><SelectValue placeholder="Choose task" /></SelectTrigger>
                                    <SelectContent>
                                        {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </F>
                        )}
                        <F label="Category">
                            <Select
                                value={uploadForm.category || "none"}
                                onValueChange={(v) => setUploadForm({ ...uploadForm, category: v === "none" ? "" : v })}
                            >
                                <SelectTrigger><SelectValue placeholder="Choose category" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {allCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Description">
                            <Textarea
                                rows={2}
                                value={uploadForm.description}
                                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                            />
                        </F>
                        {isAdmin && (
                            <F label="Visibility">
                                <Select
                                    value={uploadForm.visibility}
                                    onValueChange={(v) => setUploadForm({ ...uploadForm, visibility: v })}
                                >
                                    <SelectTrigger data-testid="upload-visibility"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin_only">Admin only</SelectItem>
                                        <SelectItem value="assigned_employee">Assigned employees</SelectItem>
                                        <SelectItem value="entire_firm">Entire firm</SelectItem>
                                        <SelectItem value="custom">Custom users</SelectItem>
                                    </SelectContent>
                                </Select>
                            </F>
                        )}
                        {isAdmin && uploadForm.visibility === "custom" && (
                            <F label="Share with">
                                <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto p-2 border rounded-md">
                                    {employees.map((e) => {
                                        const on = uploadForm.shared_with.includes(e.id);
                                        return (
                                            <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={on}
                                                    onChange={() =>
                                                        setUploadForm((f) => ({
                                                            ...f,
                                                            shared_with: on
                                                                ? f.shared_with.filter((x) => x !== e.id)
                                                                : [...f.shared_with, e.id],
                                                        }))
                                                    }
                                                />
                                                {e.name}
                                            </label>
                                        );
                                    })}
                                </div>
                            </F>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                        <Button
                            onClick={doUpload}
                            disabled={uploading}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="do-upload"
                        >
                            {uploading ? "Uploading…" : "Upload"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share dialog */}
            <Dialog open={!!shareDoc} onOpenChange={(o) => !o && setShareDoc(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Share / visibility</DialogTitle>
                    </DialogHeader>
                    {shareDoc && (
                        <div className="space-y-3">
                            <div className="text-sm text-slate-500">{shareDoc.name}</div>
                            <F label="Visibility">
                                <Select
                                    value={shareForm.visibility}
                                    onValueChange={(v) => setShareForm({ ...shareForm, visibility: v })}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin_only">Admin only</SelectItem>
                                        <SelectItem value="assigned_employee">Assigned employees</SelectItem>
                                        <SelectItem value="entire_firm">Entire firm</SelectItem>
                                        <SelectItem value="custom">Custom users</SelectItem>
                                    </SelectContent>
                                </Select>
                            </F>
                            {shareForm.visibility === "custom" && (
                                <F label="Users">
                                    <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto p-2 border rounded-md">
                                        {employees.map((e) => {
                                            const on = (shareForm.shared_with || []).includes(e.id);
                                            return (
                                                <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={on}
                                                        onChange={() =>
                                                            setShareForm((f) => ({
                                                                ...f,
                                                                shared_with: on
                                                                    ? (f.shared_with || []).filter((x) => x !== e.id)
                                                                    : [...(f.shared_with || []), e.id],
                                                            }))
                                                        }
                                                    />
                                                    {e.name}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </F>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShareDoc(null)}>Cancel</Button>
                        <Button onClick={saveShare} className="bg-primary hover:bg-primary/90">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rename dialog */}
            <Dialog open={!!renameDoc} onOpenChange={(o) => !o && setRenameDoc(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Rename document</DialogTitle>
                    </DialogHeader>
                    <Input value={renameName} onChange={(e) => setRenameName(e.target.value)} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameDoc(null)}>Cancel</Button>
                        <Button onClick={doRename} className="bg-primary hover:bg-primary/90">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History dialog */}
            <Dialog open={!!historyDoc} onOpenChange={(o) => !o && setHistoryDoc(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display">Version history</DialogTitle>
                    </DialogHeader>
                    <ul className="space-y-2">
                        {historyItems.map((v, i) => (
                            <li key={i} className="flex items-center gap-3 p-3 border rounded-md">
                                <div className="w-8 h-8 rounded bg-slate-100 grid place-items-center text-slate-600 text-xs font-semibold">
                                    v{v.version}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {v.original_name}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {(v.size / 1024).toFixed(1)} KB · {fmtDateTime(v.created_at)} · by {v.uploaded_by_name || "—"}
                                    </div>
                                </div>
                                {v.current && (
                                    <span className="badge-status bg-emerald-50 text-emerald-700 border-emerald-200">
                                        Current
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </DialogContent>
            </Dialog>

            {/* Request dialog (admin) */}
            <Dialog open={reqOpen} onOpenChange={setReqOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Request document from employee</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <F label="Employee">
                            <Select value={reqForm.employee_id} onValueChange={(v) => setReqForm({ ...reqForm, employee_id: v })}>
                                <SelectTrigger data-testid="req-emp"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                                <SelectContent>
                                    {employees.filter((e) => e.role === "employee").map((e) => (
                                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Client (optional)">
                            <Select value={reqForm.client_id || "none"} onValueChange={(v) => setReqForm({ ...reqForm, client_id: v === "none" ? "" : v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Title">
                            <Input value={reqForm.title} onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })} placeholder="e.g. Q4 GST invoices" />
                        </F>
                        <F label="Required documents (comma or newline separated)">
                            <Textarea rows={2} value={reqForm.required_docs} onChange={(e) => setReqForm({ ...reqForm, required_docs: e.target.value })} placeholder="Sales Register, Purchase Register, Bank Statement" />
                        </F>
                        <F label="Due date">
                            <Input type="date" value={reqForm.due_date} onChange={(e) => setReqForm({ ...reqForm, due_date: e.target.value })} />
                        </F>
                        <F label="Remarks">
                            <Textarea rows={2} value={reqForm.remarks} onChange={(e) => setReqForm({ ...reqForm, remarks: e.target.value })} />
                        </F>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReqOpen(false)}>Cancel</Button>
                        <Button onClick={submitRequest} className="bg-primary hover:bg-primary/90" data-testid="submit-request">
                            Send request
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const IconBtn = ({ children, onClick, title, danger }) => (
    <button
        onClick={onClick}
        title={title}
        className={`h-7 w-7 grid place-items-center rounded hover:bg-slate-100 transition-colors ${
            danger ? "text-rose-600 hover:bg-rose-50" : "text-slate-600"
        }`}
    >
        {children}
    </button>
);

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);

const FilterSelect = ({ value, onChange, label, all, children }) => (
    <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-40">
            <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
            {all && <SelectItem value="all">All {label.toLowerCase()}</SelectItem>}
            {children}
        </SelectContent>
    </Select>
);
